import {Component, OnInit, ViewChild} from '@angular/core';
import {PubDtoModel} from "../shared/models/pub.dto.model";
import {PubService} from "../shared/services/pub.service";
import {PaginationArgs, PaginationPage} from "../shared/models/pagination.args";
import {LoaderService} from "../shared/services/loader.service";
import {LocalizationService} from "../shared/services/localization.service";
import {UntypedFormBuilder, Validators} from "@angular/forms";
import {FileUpload} from "primeng/fileupload";
import {ErrorStateMatcher} from "@angular/material/core";
import {ConfirmationService, MessageService} from "primeng/api";
import {FileService} from "../shared/services/file.service";

@Component({
    selector: 'njoy-pub',
    templateUrl: './pub.component.html',
    styleUrls: ['./pub.component.scss']
})
export class PubComponent implements OnInit {

    public pubs: PubDtoModel[] = [];
    public newPub: PubDtoModel;
    public selectedPub: PubDtoModel;

    public addNewPubDialog = false;

    public paginationArgs: PaginationArgs = {pageNumber: 0, pageSize: 10};
    public totalRecords = 0;
    public first = 0;
    public row = 0;

    public modalTitle = '';
    public pubForm;
    @ViewChild('fileUpload')
    public fileUploader: FileUpload;
    public matcher = new ErrorStateMatcher();
    public uploadedFile;
    public noFileUploaded = false;
    public displayImage = false;

    constructor(private pubService: PubService,
                private localizationService: LocalizationService,
                private formBuilder: UntypedFormBuilder,
                private loaderService: LoaderService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                private fileService: FileService) {
    }

    ngOnInit(): void {
        this.findAllPubsDTO();

    }

    public addNew(): void {
        this.uploadedFile = undefined;
        this.modalTitle = this.localizationService.translate('PUB.ADD_NEW_MODAL_TITLE');
        this.addNewPubDialog = true;
        this.displayImage = false;
        this.noFileUploaded = false;
        this.initEmtpyFormValue();
    }

    get getControl() {
        return this.pubForm.controls;
    }

    private findAllPubsDTO(): void {
        this.loaderService.startLoading();
        this.pubService.findAllPubsDTO(this.paginationArgs).subscribe(
            (data: PaginationPage<PubDtoModel>) => {
                this.pubs = data.content;
                this.loaderService.stopLoading();
            }, ((err: any) => {
                this.loaderService.stopLoading();
                console.log(err);
            })
        );
    }

    private initEmtpyFormValue(pub?: PubDtoModel): void {
        this.pubForm = this.formBuilder.group({
            url: [pub ? pub.pubURL : '', [
                Validators.required
            ]]
        });
        this.selectedPub = pub ? {...pub} : undefined;
    }

    public openUpdateModal(pub: PubDtoModel): void {
        this.modalTitle = this.localizationService.translate('PUB.UPDATE_MODAL_TITLE');
        this.addNewPubDialog = true;
        this.displayImage = true;
        this.initEmtpyFormValue(pub);
    }

    public addOrUpdatePub(): void {
        this.displayRequiredMessage();
        if (this.pubForm.status === 'VALID') {
            if (this.modalTitle === this.localizationService.translate('PUB.ADD_NEW_MODAL_TITLE')) {
                this.uploadFileAndAddNewPub();
            } else {
                this.uploadFileAndUpdatePub();
            }
        }
    }

    public onUpload(event): void {
        this.noFileUploaded = false;
        this.uploadedFile = event.files[0];
    }

    public clearFile(): void {
        this.uploadedFile = undefined;
    }

    private displayRequiredMessage(): void {
        this.pubForm.touched = true;
        this.noFileUploaded = false;
        const keys = Object.keys(this.pubForm.controls);
        keys.forEach((element: any) => {
            this.pubForm.controls[element].touched = true;
        });
        if (!this.uploadedFile) {
            this.noFileUploaded = true;
        }
    }

    private uploadFileAndAddNewPub(): void {
        if (!this.noFileUploaded) {
            this.loaderService.startLoading();
            this.fileService.uploadPubFile(this.uploadedFile).subscribe(
                (path: string) => {
                    this.patchValues();
                    this.addNewPub(path);
                }, (err: any) => {
                    this.loaderService.stopLoading();
                    console.log(err)
                }
            );
        }
    }

    private uploadFileAndUpdatePub(): void {
        if (this.uploadedFile) {
            this.loaderService.startLoading();
            this.fileService.uploadOfferFile(this.uploadedFile).subscribe(
                (path: string) => {
                    this.patchValues(true);
                    this.updatePub(path);
                }, (err: any) => {
                    this.loaderService.stopLoading();
                    console.log(err)
                }
            );
        } else {
            this.patchValues(true);
            this.updatePub();
        }
    }

    private patchValues(update?: boolean): void {
        if (update) {
            this.selectedPub.pubURL = this.pubForm.value.url;
        } else {
            this.newPub = new PubDtoModel();
            this.newPub.pubURL = this.pubForm.value.url;
        }
    }

    private addNewPub(imagePath?: string): void {
        this.newPub.imagePath = imagePath;
        this.pubService.persistPub(this.newPub).subscribe(
            () => {
                this.loaderService.stopLoading();
                this.displaySuccessMessage('CLIENT.SUCCESS_SUMMARY', 'PUB.ADD_SUCCESS_DETAIL');
            }, (err: any) => {
                console.log(err);
                this.loaderService.stopLoading();
            }
        );
    }

    private updatePub(imagePath?: string): void {
        if (imagePath) {
            this.selectedPub.imagePath = imagePath;
        }
        this.pubService.updatePub(this.selectedPub).subscribe(
            () => {
                this.loaderService.stopLoading();
                this.displaySuccessMessage('CLIENT.SUCCESS_SUMMARY', 'PUB.UPDATE_SUCCESS_DETAIL');
            }, (err: any) => {
                console.log(err);
                this.loaderService.stopLoading();
            }
        );
    }

    public changeImageValue(): void {
        this.displayImage = false;
        setTimeout(() => this.fileUploader.advancedFileInput.nativeElement.click(), 100);
    }

    private displaySuccessMessage(summary: string, detail: string): void {
        this.messageService.add({
            severity: 'success',
            summary: this.localizationService.translate(summary),
            detail: this.localizationService.translate(detail)
        });
        this.addNewPubDialog = false;
        this.findAllPubsDTO();
    }

    public confirmDeletePub(pub: PubDtoModel): void {
        this.confirmationService.confirm({
            message: this.localizationService.translate('PUB.DELETE_CONFIRMATION_MESSAGE') + pub.pubURL,
            accept: () => {
                //Actual logic to perform a confirmation
                this.deletePub(pub.uuid);
            }
        });
    }

    private deletePub(uuid: string): void {
        this.pubService.deletePub(uuid).subscribe(
            () => {
                this.loaderService.stopLoading();
                this.displaySuccessMessage('CLIENT.SUCCESS_SUMMARY', 'PUB.DELETE_SUCCESS_DETAIL');
            }, (err: any) => {
                console.log(err);
                this.loaderService.stopLoading();
            }
        );
    }
}
